import { supabase } from './supabase';
import { addDays, addYears, differenceInDays, parseISO } from 'date-fns';

export const REVIEW_TYPES = [
  { key: 'probationary_90',     label: '90-Day Probationary' },
  { key: 'annual',              label: 'Annual Review' },
  { key: 'supervisor_initiated', label: 'Supervisor-Initiated' },
  { key: 'pip',                 label: 'Performance Improvement Plan' },
];
export const REVIEW_TYPE_LABEL = Object.fromEntries(REVIEW_TYPES.map(t => [t.key, t.label]));

export const REVIEW_COMPETENCIES = [
  { key: 'quality_of_care',           label: 'Quality of Care' },
  { key: 'reliability_attendance',    label: 'Reliability & Attendance' },
  { key: 'documentation_compliance',  label: 'Documentation Compliance' },
  { key: 'communication',             label: 'Communication' },
  { key: 'teamwork',                  label: 'Teamwork' },
];

export const getPerformanceReviews = () =>
  supabase.from('performance_reviews')
    .select('*, staff:staff(full_name, first_name, last_name, start_date)')
    .order('review_date', { ascending: false });

export const getStaffPerformanceReviews = (staffId) =>
  supabase.from('performance_reviews')
    .select('*')
    .eq('staff_id', staffId)
    .order('review_date', { ascending: false });

const meanRating = (ratings) => {
  const vals = Object.values(ratings || {}).map(Number).filter(v => !isNaN(v) && v > 0);
  if (!vals.length) return null;
  return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
};

export const addPerformanceReview = (payload) =>
  supabase.from('performance_reviews').insert([{ ...payload, overall_rating: meanRating(payload.ratings) }]).select().single();

export const updatePerformanceReview = (id, payload) =>
  supabase.from('performance_reviews').update({ ...payload, overall_rating: meanRating(payload.ratings), updated_at: new Date().toISOString() }).eq('id', id).select().single();

// Mirrors certificationStatus() in ./training.js — same color tokens/thresholds,
// applied to "when is this person's next review due" instead of cert expiry.
export const reviewDueStatus = (staffMember, staffReviews) => {
  if (!staffMember?.start_date) {
    return { key: 'unknown', label: 'No start date on file', color: 'var(--text3)', bg: 'var(--bg2)', dueDate: null };
  }
  const today = new Date();
  const hireDate = parseISO(staffMember.start_date);
  const oneYearAfterHire = addYears(hireDate, 1);
  const hasProbationary = (staffReviews || []).some(r => r.review_type === 'probationary_90');

  let dueDate;
  if (today < oneYearAfterHire && !hasProbationary) {
    dueDate = addDays(hireDate, 90);
  } else {
    const annualReviews = (staffReviews || [])
      .filter(r => r.review_type === 'annual')
      .sort((a, b) => new Date(b.review_date) - new Date(a.review_date));
    dueDate = annualReviews.length ? addYears(parseISO(annualReviews[0].review_date), 1) : oneYearAfterHire;
  }

  const daysUntilDue = differenceInDays(dueDate, today);
  if (daysUntilDue < 0) return { key: 'overdue', label: 'Overdue', color: 'var(--danger)', bg: 'var(--danger-bg)', dueDate };
  if (daysUntilDue <= 30) return { key: 'due_soon', label: 'Due Soon', color: 'var(--warn)', bg: 'var(--warn-bg)', dueDate };
  return { key: 'ok', label: 'On Track', color: 'var(--success)', bg: 'var(--success-bg)', dueDate };
};
